import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import SearchArea from "./SearchArea";
import DesignCard from "./DesignCard";
import DesignPreviewModal from "./components/DesignPreviewModal";
import useDesignManagement, { Design, DESIGN_STATUS, DesignStatus, PreparedDesignForEdit } from "./useDesignManagement";
import { useStore } from "../../../store";
import { StoreType } from "../../../store/types";
import { getAllowedFormats } from "pages/flyer/builder/components/SideBar/Aside/components/Settings/Settings";
import { FORMATS } from "pages/flyer/builder/constants";
import { generatePath, useHistory } from "react-router";
import ROUTES from "../../../config/routing";
import { useDownloadDesign } from "./utils/useDownloadDesign";
import type { DesignPdfLinks } from "./utils/downloadDesign";
import { buildPreparedEditSearch, getBuilderImportParams } from "./utils/builderParams";
import { shouldAutoAddAddressBox, useFigmaEditChoice } from "./utils/useFigmaEditChoice";
import { FigmaEditDialog } from "./components";
import { toFigmaEditCopyInput } from "./components/figmaEditCopy";

interface DesignGalleryProps {
  activeDesign: Design | null;
  onDesignSelect: (design: Design) => void;
}

const PAGE_SIZE = 25;

const DesignGallery: React.FC<DesignGalleryProps> = ({ activeDesign, onDesignSelect }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [selectedDesignForPreview, setSelectedDesignForPreview] = useState<Design | null>(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  // Whether the gallery grid has been scrolled past the top. Drives
  // the opacity of the top-edge fade overlay so users get a visual
  // hint that content is hidden above the current scroll position —
  // designs were silently clipped before without any affordance.
  const [isScrolledFromTop, setIsScrolledFromTop] = useState(false);
  const gridRef = useRef<HTMLDivElement | null>(null);

  // Scroll listener for the gallery grid. We flip the boolean rather
  // than recomputing on every render (cheap state, debounce-free) —
  // any scrollTop > 0 enables the fade, scrollTop === 0 disables it.
  // No raf throttle because the change is binary and React 18's
  // auto-batching collapses repeat sets into a single render.
  const handleGridScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const shouldShow = e.currentTarget.scrollTop > 0;
    setIsScrolledFromTop((prev) => (prev === shouldShow ? prev : shouldShow));
  }, []);
  const {
    client: { id: clientId },
    campaign: { id: campaignId, channel, subtype, flyerWeight },
    costsCalculationData: { printingCosts },
    country,
  }: StoreType = useStore();
  const { designs, setDesigns, loading, duplicateDesign, archiveDesign, fetchDesigns } = useDesignManagement({
    clientId,
  });
  const history = useHistory();
  const isPublicBuilder = !(channel || subtype);

  const allowedFormats = getAllowedFormats({
    channel,
    subtype,
    countryCode: country.code,
    printingCosts,
    flyerWeight,
    isPublicBuilder,
  });

  // Memoized search function for better performance
  const searchDesigns = useCallback((designs: Design[], query: string): Design[] => {
    if (!query.trim()) {
      return designs;
    }

    const normalizedQuery = query.toLowerCase().trim();

    return designs.filter((design) => {
      // Defensive checks for potentially undefined properties
      const designName = design?.designName?.toLowerCase() || "";
      const format = design?.format?.toLowerCase() || "";
      const designer = design?.designer?.toLowerCase() || "";

      return (
        designName.includes(normalizedQuery) || format.includes(normalizedQuery) || designer.includes(normalizedQuery)
      );
    });
  }, []);

  const filteredDesigns = useMemo(() => {
    if (!Array.isArray(designs)) {
      return [];
    }

    const filtered = designs.filter((design) => design.status === DESIGN_STATUS.ACTIVE);

    return searchDesigns(filtered, searchQuery);
  }, [designs, searchQuery, searchDesigns]);

  // Memoized sorting function
  const sortedDesigns = useMemo(() => {
    return [...filteredDesigns].sort((a, b) => {
      const aAllowed = allowedFormats.includes(a.format);
      const bAllowed = allowedFormats.includes(b.format);

      if (aAllowed !== bAllowed) {
        return aAllowed ? -1 : 1;
      }

      try {
        const dateA = a?.dateCreated instanceof Date ? a.dateCreated : new Date(a?.dateCreated || 0);
        const dateB = b?.dateCreated instanceof Date ? b.dateCreated : new Date(b?.dateCreated || 0);
        return dateB.getTime() - dateA.getTime();
      } catch (error) {
        console.warn("Error sorting designs by date:", error);
        return 0;
      }
    });
  }, [filteredDesigns, allowedFormats]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
    // Imperatively reset the grid's scroll position when the filtered
    // result set changes. The browser auto-clamps `scrollTop` to 0 if
    // the new (smaller) list no longer overflows, BUT it does NOT
    // fire a scroll event in that case — so the fade-overlay's
    // `isScrolledFromTop` state would stay `true` and the shadow
    // would persist over a list that's actually at the top. Clearing
    // both keeps DOM scroll and React state in sync.
    if (gridRef.current) gridRef.current.scrollTop = 0;
    setIsScrolledFromTop(false);
  }, [searchQuery, filteredDesigns.length]);

  const visibleDesigns = useMemo(() => {
    return sortedDesigns.slice(0, visibleCount);
  }, [sortedDesigns, visibleCount]);

  const hasMoreDesigns = visibleCount < sortedDesigns.length;

  const handleLoadMore = useCallback(() => {
    setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, sortedDesigns.length));
  }, [sortedDesigns.length]);

  // Modal and action handlers
  const handlePreview = useCallback((design: Design) => {
    try {
      setSelectedDesignForPreview(design);
      setPreviewModalOpen(true);
    } catch (error) {
      console.error("Error opening preview:", error);
    }
  }, []);

  // Keep the generated links locally so the next Download opens the stored PDF directly.
  const storeGeneratedLinks = useCallback(
    (designId: string, links: DesignPdfLinks) => {
      const update = { s3Link: links.s3Link ?? undefined, s3CmykLink: links.s3CmykLink ?? undefined };
      setDesigns((prev) => prev.map((design) => (design.id === designId ? { ...design, ...update } : design)));
      setSelectedDesignForPreview((prev) => (prev?.id === designId ? { ...prev, ...update } : prev));
    },
    [setDesigns]
  );
  const handleDownload = useDownloadDesign({ onGenerated: storeGeneratedLinks });

  // Figma designs: the dialog asks for copy or flatten before the design tool opens (PAD-32396).
  const [figmaEditDesign, setFigmaEditDesign] = useState<Design | null>(null);

  const flyerBuilderPathFor = useCallback(
    (format: string) => generatePath(ROUTES.FLYER_BUILDER, { clientId, campaignId, format }),
    [clientId, campaignId]
  );

  const handleEdit = useCallback(
    (design: Design) => {
      try {
        const importParams = getBuilderImportParams(design);
        if (importParams.kind === "needsFigmaChoice") {
          setFigmaEditDesign(design);
          return;
        }
        // An uploaded PDF that was never edited is imported onto the canvas, like the platform does.
        const search =
          importParams.kind === "pdfImport"
            ? buildPreparedEditSearch({ designId: design.id, fileLink: importParams.fileLink })
            : `?designId=${design.id}`;
        // Open the flyer builder in a new tab for editing the design
        window.open(`${flyerBuilderPathFor(design.format)}${search}`, "_blank");
      } catch (error) {
        console.error("Error opening design editor:", error);
      }
    },
    [flyerBuilderPathFor]
  );

  const pendingEditTabRef = useRef<Window | null>(null);

  const openPendingEditTab = useCallback(() => {
    const tab = window.open("about:blank", "_blank");
    if (tab) {
      tab.opener = null;
      try {
        tab.document.title = "Preparing your design";
        tab.document.body.textContent = "Preparing your design. This can take a few seconds.";
      } catch {
        // The placeholder text is cosmetic; the tab still receives the builder URL.
      }
    }
    pendingEditTabRef.current = tab;
  }, []);

  const closePendingEditTab = useCallback(() => {
    pendingEditTabRef.current?.close();
    pendingEditTabRef.current = null;
  }, []);

  const openPreparedDesign = useCallback(
    (prepared: PreparedDesignForEdit) => {
      if (!figmaEditDesign) return;
      const url = `${flyerBuilderPathFor(figmaEditDesign.format)}${buildPreparedEditSearch({
        designId: prepared.designId,
        fileLink: prepared.fileLink,
        autoAddAddressBox: shouldAutoAddAddressBox(figmaEditDesign),
      })}`;
      const tab = pendingEditTabRef.current;
      pendingEditTabRef.current = null;
      if (tab && !tab.closed) tab.location.href = url;
      else window.open(url, "_blank");
      setFigmaEditDesign(null);
      // The copy is a new design, so the gallery lists it next to the untouched original.
      fetchDesigns();
    },
    [figmaEditDesign, flyerBuilderPathFor, fetchDesigns]
  );

  const figmaEditChoice = useFigmaEditChoice({
    onStart: openPendingEditTab,
    onPrepared: openPreparedDesign,
    onFailed: closePendingEditTab,
  });

  const closeFigmaEditDialog = useCallback(() => {
    setFigmaEditDesign(null);
    figmaEditChoice.reset();
  }, [figmaEditChoice.reset]);

  const closePreviewModal = useCallback(() => {
    setPreviewModalOpen(false);
    setSelectedDesignForPreview(null);
  }, []);

  const handleDesignSelect = useCallback(
    (design: Design) => {
      try {
        onDesignSelect(design);
      } catch (error) {
        console.error("Error selecting design:", error);
      }
    },
    [onDesignSelect]
  );

  // Loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-10 bg-gray-200 rounded mb-4"></div>
          <div className="grid grid-cols-2 gap-2">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="h-48 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (!sortedDesigns.length) {
    return (
      <div className="space-y-6">
        <SearchArea onSearch={setSearchQuery} />
        <div className="text-center py-8 text-gray-500">
          {searchQuery.trim() ? "No designs found matching your search." : "No designs available."}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" role="region" aria-label="Design Gallery">
      <SearchArea onSearch={setSearchQuery} />
      <div className="relative w-[670px]">
        {/* Top-edge fade — visible only once the grid has been scrolled
            past its starting position. Signals to the user that
            content is hidden above the current viewport so the cards
            don't appear to be silently clipped at the top edge.
            `pointer-events-none` keeps scroll wheel + clicks reaching
            the grid below. Height (24px) is enough to cover the first
            row of fading cards without obscuring the next row. */}
        <div
          aria-hidden
          className="pointer-events-none absolute top-0 left-0 right-0 z-10 transition-opacity duration-150"
          style={{
            height: 24,
            opacity: isScrolledFromTop ? 1 : 0,
            // Dark-tinted shadow gradient (vs. a white "fade-out")
            // reads as a clear "content casts a shadow at the top
            // edge" affordance — the prior translucent-white version
            // was nearly invisible against the white grid background.
            // Brand navy (#0C172C) at 0.10 → transparent over 24px.
            background: "linear-gradient(to bottom, rgba(12, 23, 44, 0.10), rgba(12, 23, 44, 0))",
            // Match the grid's border radius at the top edge so the
            // fade doesn't bleed past the container's rounded corners.
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
          }}
        />
        <div
          ref={gridRef}
          onScroll={handleGridScroll}
          // 8px radius matches the surrounding gallery card's rounded
          // corner so design thumbnails are clipped to the same arc
          // when they scroll past the top edge — previously they bled
          // a hairline past the corner because the scrollable grid had
          // no radius of its own.
          className="grid grid-cols-2 gap-2 w-full max-h-[560px] overflow-y-auto rounded-lg"
          // Picking artwork is a single choice from a list, so listbox/option
          // is the honest pattern — a grid implies two-dimensional navigation
          // this never had. It also gives assistive tech, and Copilot control
          // mode, a selected state to read instead of inferring one from
          // styling.
          role="listbox"
          aria-label="Design cards"
          // Permanent, always-open — Copilot control mode must never mistake
          // this for a transient dropdown when matching `ui_select` options.
          data-copilot-options="ignore"
        >
          {visibleDesigns.map((design, index) => {
            // Defensive property access with fallbacks
            const designId = design?.id || `design-${index}`;
            const designName = design?.designName || "Untitled Design";
            const dateCreated = design?.dateCreated;
            const isFormatAllowed = allowedFormats.includes(design.format);

            const imageSrc = design?.imageSrc || "";
            const status = design?.status || DESIGN_STATUS.ARCHIVED;

            // Safe date formatting
            let formattedDate = "";
            try {
              if (dateCreated) {
                let locale = "en-US";
                try {
                  locale = navigator.languages[0] || "en-US";
                } catch (error) {
                  console.warn("Error getting locale:", error);
                }

                const date = new Date(dateCreated);
                if (!isNaN(date.getTime())) {
                  formattedDate = date.toLocaleDateString(locale, {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                    hour: "numeric",
                    minute: "numeric",
                  });
                }
              }
            } catch (error) {
              console.warn("Error formatting date for design:", designName, error);
              // Don't set any date if there's an error
              formattedDate = "";
            }

            return (
              <DesignCard
                key={designId}
                title={designName}
                date={formattedDate}
                isUpdated={status === DESIGN_STATUS.ACTIVE}
                hasMultiple={false}
                image={imageSrc}
                isActive={activeDesign?.id === designId}
                isDisabled={!isFormatAllowed}
                onClick={() => {
                  if (isFormatAllowed) {
                    handleDesignSelect(design);
                  }
                }}
                design={design}
                onPreview={handlePreview}
                onDownload={handleDownload}
                onEdit={handleEdit}
              />
            );
          })}
        </div>
      </div>

      {hasMoreDesigns && (
        <div className="flex justify-center">
          <button
            type="button"
            className="design-gallery__load-more px-4 py-2 text-sm font-semibold rounded-md"
            onClick={handleLoadMore}
          >
            Load more
          </button>
        </div>
      )}

      {figmaEditDesign && (
        <FigmaEditDialog
          open
          {...toFigmaEditCopyInput(figmaEditDesign)}
          busyChoice={figmaEditChoice.busyChoice}
          showError={figmaEditChoice.showError}
          onClose={closeFigmaEditDialog}
          onEditCopy={() => figmaEditChoice.choose(figmaEditDesign, "copy")}
          onFlattenOriginal={() => figmaEditChoice.choose(figmaEditDesign, "flatten")}
        />
      )}

      {/* Preview Modal */}
      <DesignPreviewModal
        isOpen={previewModalOpen}
        onClose={closePreviewModal}
        design={selectedDesignForPreview}
        onDownload={handleDownload}
      />
    </div>
  );
};

export default DesignGallery;
